import api_client from '@/repositories';
import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

export interface IUseListResourceGroupsQueryArgs
	extends UseQueryOptions<unknown, unknown, Awaited<ReturnType<typeof api_client.resourceGroups.list>>> {}

export function useListResourceGroupsQuery(args?: IUseListResourceGroupsQueryArgs) {
	return useQuery({
		queryKey: ['resource-groups', 'list'],
		queryFn: api_client.resourceGroups.list,
		...args,
	});
}

export function usePrefetchListResourceGroups() {
	return useQueryClient().prefetchQuery({
		queryKey: ['resource-groups', 'list'],
		queryFn: api_client.resourceGroups.list,
	});
}

export interface IUseResourceGroupsDetailsQueryArgs
	extends UseQueryOptions<Awaited<ReturnType<typeof api_client.resourceGroups.details>>> {
	id: string;
}

export function useResourceGroupsDetailsQuery(args: IUseResourceGroupsDetailsQueryArgs) {
	return useQuery({
		queryKey: ['resource-groups', 'details', args.id],
		queryFn: () => api_client.resourceGroups.details({ id: args.id }),
		...args,
	});
}

export interface IUseCreateResourceGroupMutationArgs
	extends UseMutationOptions<unknown, unknown, Parameters<typeof api_client.resourceGroups.create>[0]> {}

export function useCreateResourceGroupMutation(args?: IUseCreateResourceGroupMutationArgs) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: api_client.resourceGroups.create,
		onSuccess: function (data, variables, context) {
			queryClient.invalidateQueries(['resource-groups', 'details', variables.id]);
			queryClient.invalidateQueries(['resource-groups', 'list']);
			args?.onSuccess?.(data, variables, context);
		},
		...args,
	});
}

interface IUseRemoveResourceGroupMutationArgs
	extends UseMutationOptions<void, unknown, Parameters<typeof api_client.resourceGroups.remove>[0]> {}

export function useRemoveResourceGroupMutation(args?: IUseRemoveResourceGroupMutationArgs) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: api_client.resourceGroups.remove,
		onSuccess: function (data, variables, context) {
			queryClient.invalidateQueries(['resource-groups', 'details', variables.id]);
			queryClient.invalidateQueries(['resource-groups', 'list']);
			args?.onSuccess?.(data, variables, context);
		},
		...args,
	});
}
